import type { UnknownAction } from "@reduxjs/toolkit";
import type { MenuItem } from "@v1/components/designSystem/menu/Menu";
import type { UseApplyComponentActionType } from "@v1/hooks/useApplyComponentAction";
import type { Operator } from "replo-runtime/shared/enums";
import type {
  ConditionStatement,
  EditVariantConditionValueProps,
  PredefinedVariantType,
  VariantQuery,
  VariantWithState,
} from "replo-runtime/shared/types";
import type { Component } from "schemas/component";
import type { ConditionField, ReploState } from "schemas/generated/symbol";

import * as React from "react";

import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import { setDraftElement } from "@v1/actions/core-actions";
import ButtonGroupComponent from "@v1/components/common/designSystem/ButtonGroup";
import DeprecatedLabeledControl from "@v1/components/common/designSystem/DeprecatedLabeledControl";
import Input from "@v1/components/common/designSystem/Input";
import { Badge } from "@v1/components/designSystem/badge/Badge";
import DeprecatedButton from "@v1/components/designSystem/button/Button";
import DeprecatedIconButton from "@v1/components/designSystem/button/IconButton";
import SelectableChip from "@v1/components/designSystem/chip/SelectableChip";
import { Menu, MenuTrigger } from "@v1/components/designSystem/menu/Menu";
import Popover from "@v1/components/designSystem/popover/Popover";
import Selectable from "@v1/components/designSystem/Selectable";
import SwitchWithDescription from "@v1/components/designSystem/switch/SwitchWithDescription";
import {
  getConditionFieldEditorData,
  operationToSymbol,
} from "@v1/components/editor/condition";
import { NEW_STATE_NAME } from "@v1/components/editor/constants";
import ModifierGroup from "@v1/components/editor/page/element-editor/components/extras/ModifierGroup";
import { ProductSelectionCombobox } from "@v1/components/editor/page/ProductSelectionCombobox";
import { ProductVariantSelectionCombobox } from "@v1/components/editor/page/ProductVariantSelectionCombobox";
import useApplyComponentAction from "@v1/hooks/useApplyComponentAction";
import { useOverridableInput } from "@v1/hooks/useOverridableInput";
import useSetDraftElement from "@v1/hooks/useSetDraftElement";
import {
  selectAnimateVariantTransitions,
  selectAreMultipleDraftComponents,
  selectAreMultipleDraftComponentsInDefaultVariant,
  selectDraftComponent,
  selectDraftComponentConditionFields,
  selectDraftComponentId,
  selectDraftComponentVariants,
  selectNearestAncestorComponentWithVariants,
  selectNearestComponentWithVariantsId,
  selectPredefinedVariantsForDraftComponent,
  setComponentVariant as setComponentVariantAction,
} from "@v1/reducers/core-reducer";
import { useEditorDispatch, useEditorSelector } from "@v1/store/store";
import {
  SortableItem,
  SortableList,
} from "@v2/features/products/components/SortableList";
import { useAreModalsOpen } from "@v2/store/modalStore";
import { docs } from "@v2/utils/docs";
import isNumber from "lodash-es/isNumber";
import startCase from "lodash-es/startCase";
import { Minus, Pencil, Plus } from "lucide-react";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";
import { mapNull } from "replo-runtime/shared/utils/optional";
import { isMixedStyleValue } from "replo-runtime/store/utils/mixed-values";
import {
  exhaustiveSwitch,
  hasOwnProperty,
  isEmpty,
} from "replo-utils/lib/misc";
import { useDebouncedCallback } from "replo-utils/react/use-debounced-callback";
import { v4 as uuidv4 } from "uuid";

import DocumentationInfoIcon from "../DocumentationInfoIcon";
import ModifierLabel from "../extras/ModifierLabel";

type VariantComponentActionType =
  | {
      componentActionType: "addVariant";
      predefinedVariantType?: never;
    }
  | {
      componentActionType: "addPredefinedVariant";
      predefinedVariantType: PredefinedVariantType;
    };

type VariantData = {
  query: VariantQuery | null;
  name: string;
} & VariantComponentActionType;

const VariantModifier: React.FC = () => {
  const [currentEditingVariantId, setCurrentEditingVariantId] = React.useState<
    string | null
  >(null);

  const applyComponentAction = useApplyComponentAction();

  const setDraftElement = useSetDraftElement();
  const areMultipleDraftComponents = useEditorSelector(
    selectAreMultipleDraftComponents,
  );
  const variants = useEditorSelector(selectDraftComponentVariants);
  const areMultipleDraftComponentsInDefaultVariant = useEditorSelector(
    selectAreMultipleDraftComponentsInDefaultVariant,
  );

  const hasVariants = isMixedStyleValue(variants) || variants.length > 0;
  const [isOpen, setIsOpen] = useOverridableState(hasVariants);

  const showMixedVariantsWarning =
    !areMultipleDraftComponentsInDefaultVariant && isMixedStyleValue(variants);

  const showDefaultVariant =
    areMultipleDraftComponentsInDefaultVariant ||
    (!isMixedStyleValue(variants) && variants.length === 0);

  const showVariantsList =
    !areMultipleDraftComponentsInDefaultVariant &&
    !isMixedStyleValue(variants) &&
    variants.length > 0;

  const addNewVariant = React.useCallback(
    (variantData: VariantData) => {
      const { query, name, componentActionType } = variantData;

      const newVariant = {
        query,
        name,
        id: uuidv4(),
      };

      exhaustiveSwitch({ type: componentActionType })({
        addVariant: () => {
          return applyComponentAction({
            type: "addVariant",
            value: { variant: newVariant },
          });
        },
        addPredefinedVariant: () => {
          return applyComponentAction({
            type: "addPredefinedVariant",
            value: {
              variant: newVariant,
              predefinedVariantType: variantData.predefinedVariantType,
            },
          });
        },
      });

      setDraftElement({ variantId: newVariant.id });

      // Note (Fran, 2024-12-04): We don't want to show the edit variant menu if the variant has
      // a trigger or a condition statement already set.
      if (query === null) {
        setCurrentEditingVariantId(newVariant.id);
      }
    },
    [applyComponentAction, setDraftElement],
  );

  return (
    <div className="flex flex-col gap-2">
      <AncestorVariants />
      <ModifierGroup
        titleEnhancer={
          <DocumentationInfoIcon href="https://support.replo.app/articles/1713125668-states" />
        }
        title="States"
        isCollapsible
        isOpen={isOpen}
        onOpenChange={setIsOpen}
        endEnhancer={
          !areMultipleDraftComponents && (
            <AddVariantMenu onSelect={addNewVariant}>
              <DeprecatedIconButton
                aria-label="Add state"
                variant="tertiary"
                icon={<Plus className="text-muted" size={14} />}
                tooltipText="Add State"
                size="sm"
              />
            </AddVariantMenu>
          )
        }
      >
        {showMixedVariantsWarning && (
          <div className="bg-sky-soft text-primary flex items-center gap-1 rounded-lg p-2">
            <DocumentationInfoIcon href={docs.multiSelect.multipleStates} />
            <span className="text-xs">Mixed selected states</span>
          </div>
        )}
        {showDefaultVariant && (
          <div className="flex flex-wrap gap-2">
            <SelectableChip
              active
              size="sm"
              data-testid="variant-default"
              showHoverStateWhenActive={false}
            >
              Default
            </SelectableChip>
            {!areMultipleDraftComponentsInDefaultVariant && (
              <PredefinedVariantList onClick={addNewVariant} />
            )}
          </div>
        )}
        {showVariantsList && (
          <VariantList
            currentEditingVariantId={currentEditingVariantId}
            setCurrentEditingVariantId={setCurrentEditingVariantId}
          />
        )}
      </ModifierGroup>
    </div>
  );
};

const VariantList: React.FC<{
  currentEditingVariantId: string | null;
  setCurrentEditingVariantId: React.Dispatch<
    React.SetStateAction<string | null>
  >;
}> = ({ currentEditingVariantId, setCurrentEditingVariantId }) => {
  const applyComponentAction = useApplyComponentAction();

  const draftComponent = useEditorSelector(selectDraftComponent);
  const areModalsOpen = useAreModalsOpen();
  const variants = useEditorSelector(selectDraftComponentVariants);
  const hasMixedVariants = isMixedStyleValue(variants);
  const currentEditingVariant =
    !hasMixedVariants && currentEditingVariantId != null
      ? variants.find((v) => v.id === currentEditingVariantId)
      : null;

  if (hasMixedVariants) {
    return null;
  }

  return (
    // Popover component is needed at root in order to wrap the pane
    // and let us place the rest of the parts wherever we need them.
    <Popover.Root
      isOpen={currentEditingVariantId !== null}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          setCurrentEditingVariantId(null);
        }
      }}
    >
      <div className="flex flex-wrap gap-2">
        <SortableList
          orientation="both-axis"
          onReorderEnd={({ oldIndex, newIndex }) => {
            // Note (Noah, 2022-11-09): Don't allow reordering of something
            // to override the default state, default state is always first
            if (oldIndex !== 0 && newIndex !== 0 && newIndex !== oldIndex) {
              applyComponentAction({
                type: "reorderVariant",
                value: {
                  oldIndex,
                  newIndex,
                },
              });
            }
          }}
          activationConstraint={{
            // Note (Sebas, 2023-01-19): We have to add a little more of delay
            // because it was causing some issues when trying to select a state
            delay: 150,
            tolerance: 15,
          }}
        >
          {variants.map((variant) => (
            <SortableItem
              key={variant.id}
              id={variant.id}
              className="rounded-xl"
            >
              <VariantChip
                key={variant.id}
                variant={variant}
                onSelectEdit={(id) => {
                  setCurrentEditingVariantId?.(id);
                }}
              />
            </SortableItem>
          ))}
        </SortableList>
        <Popover.Anchor className="absolute left-0 top-0" />
        {currentEditingVariant && (
          <Popover.Content
            title="Configure State"
            shouldPreventDefaultOnInteractOutside={areModalsOpen}
          >
            <EditVariantMenu
              variants={variants}
              variant={currentEditingVariant}
              onDelete={() => {
                setCurrentEditingVariantId(null);
              }}
            />
          </Popover.Content>
        )}
      </div>
      {draftComponent && <AnimationToggle component={draftComponent} />}
    </Popover.Root>
  );
};

const getChipDropdownItems = (
  variantId: string,
  componentId: string,
  variantName: string,
  componentWithVariantsId: string,
  applyComponentAction: (action: UseApplyComponentActionType) => UnknownAction,
): MenuItem[] => {
  return [
    {
      items: [
        {
          id: "resetStateToDefault",
          title: "Reset Styles to Default State",
          type: "leaf",
          variant: "default",
          size: "sm",
          disabled: variantName === "default",
          onSelect: () => {
            applyComponentAction({
              type: "resetStateToDefault",
              variantId,
              componentId,
            });
          },
        },
        {
          id: "pushPropsToDefault",
          title: "Push Changes to Default State",
          type: "leaf",
          variant: "default",
          size: "sm",
          disabled: variantName === "default",
          onSelect: () => {
            applyComponentAction({
              type: "pushOverridePropsToDefault",
              variantId,
              componentId,
            });
          },
        },
        {
          id: "delete",
          title: "Delete State",
          type: "leaf",
          variant: "default",
          size: "sm",
          disabled: variantName === "default",
          onSelect: () => {
            applyComponentAction({
              type: "deleteVariant",
              value: { variantId },
              componentId: componentWithVariantsId,
            });
          },
        },
      ],
      type: "section",
    },
  ];
};

const AncestorVariants: React.FC = () => {
  const dispatch = useEditorDispatch();

  const { ancestorComponentId, ancestorComponentName, variantsWithState } =
    useEditorSelector(selectNearestAncestorComponentWithVariants);
  const parentHasVariants = variantsWithState.length > 0;

  const onClick = (id: string) => {
    dispatch(
      setComponentVariantAction({
        componentId: ancestorComponentId ?? undefined,
        variantId: id ?? undefined,
      }),
    );
  };

  if (!parentHasVariants) {
    return null;
  }

  return (
    <ModifierGroup title="Parent States">
      {ancestorComponentName && (
        <DeprecatedLabeledControl
          label={ancestorComponentName}
          className="mb-1 font-medium"
          size="sm"
          onClick={() => {
            if (ancestorComponentId) {
              dispatch(
                setDraftElement({
                  componentIds: [ancestorComponentId],
                }),
              );
            }
          }}
        />
      )}
      <div className="flex flex-wrap gap-2">
        {variantsWithState.map(({ isActive, id, name }) => (
          <AncestorVariantChip
            key={id}
            id={id}
            isActive={isActive}
            name={name}
            onClick={onClick}
          />
        ))}
      </div>
    </ModifierGroup>
  );
};

const ChipMenu: React.FC<
  React.PropsWithChildren<{
    id: string;
    name: string;
    onClick(id: string): void;
  }>
> = ({ id, children, name, onClick }) => {
  const applyComponentAction = useApplyComponentAction();
  const componentId = useEditorSelector(selectDraftComponentId);
  const componentWithVariantsId = useEditorSelector(
    selectNearestComponentWithVariantsId,
  );

  if (!componentId || !componentWithVariantsId) {
    return null;
  }

  return (
    <Menu
      items={getChipDropdownItems(
        id,
        componentId,
        name,
        componentWithVariantsId,
        applyComponentAction,
      )}
      customWidth={280}
      menuType="context"
      onRequestOpen={() => onClick(id)}
      trigger={<MenuTrigger className="bg-transparent">{children}</MenuTrigger>}
    />
  );
};

const AncestorVariantChip: React.FC<{
  id: string;
  name: string;
  isActive: boolean;
  onClick(id: string): void;
}> = ({ id, isActive, name, onClick }) => {
  return (
    <ChipMenu id={id} onClick={onClick} name={name}>
      <SelectableChip
        active={isActive}
        size="sm"
        onClick={() => onClick(id)}
        showHoverStateWhenActive={false}
      >
        {startCase(name)}
      </SelectableChip>
    </ChipMenu>
  );
};

const VariantChip: React.FC<{
  variant: VariantWithState;
  onSelectEdit: (id: string) => void;
}> = ({ variant: { id, name, isActive }, onSelectEdit }) => {
  const setDraftElement = useSetDraftElement();

  const isVariantDefault = name === "default";

  const onClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    // e.detail stores the amount of clicks
    switch (e.detail) {
      case 1:
        return setDraftElement({ variantId: id });
      case 2:
        return !isVariantDefault && onSelectEdit?.(id);
      default:
        return null;
    }
  };

  return (
    <ChipMenu
      id={id}
      name={name}
      onClick={(id: string) => setDraftElement({ variantId: id })}
    >
      <div className="group flex flex-wrap gap-2">
        <SelectableChip
          active={isActive}
          size="sm"
          onClick={onClick}
          data-testid={`variant-${name}`}
          showHoverStateWhenActive={false}
        >
          {startCase(name)}
        </SelectableChip>
        {!isVariantDefault && (
          <Tooltip content="Configure State" triggerAsChild>
            <Popover.Trigger asChild>
              <button
                aria-label="Configure State"
                type="button"
                onClick={() => onSelectEdit(id)}
                className={twMerge(
                  "absolute right-[-10px] top-[-8px] grid h-5 w-5 rounded-full",
                  isActive && "bg-primary visible",
                  !isActive && "bg-surface invisible group-hover:visible",
                )}
              >
                <Pencil
                  size={10}
                  color="white"
                  className="place-self-center"
                  aria-hidden
                />
              </button>
            </Popover.Trigger>
          </Tooltip>
        )}
      </div>
    </ChipMenu>
  );
};

const PredefinedVariantList: React.FC<{
  onClick(variantData: VariantData): void;
}> = ({ onClick }) => {
  const predefinedVariantsFromSelector = useEditorSelector(
    selectPredefinedVariantsForDraftComponent,
  );

  const predefinedVariants = predefinedVariantsFromSelector.map(
    (predefinedVariant) => {
      return {
        name: predefinedVariant.name,
        onClick: () => {
          onClick({
            query: predefinedVariant.query,
            name: predefinedVariant.name,
            componentActionType: "addPredefinedVariant",
            predefinedVariantType: predefinedVariant.predefinedVariantType,
          });
        },
      };
    },
  );

  return predefinedVariants.map((predefinedVariant) => {
    return (
      <Tooltip
        key={predefinedVariant.name}
        content={`Add ${predefinedVariant.name}`}
        triggerAsChild
      >
        <SelectableChip
          size="sm"
          variant="empty"
          data-testid={`variant-${predefinedVariant.name}`}
          onClick={() => {
            if ("onClick" in predefinedVariant) {
              predefinedVariant.onClick?.();
            }
          }}
          startEnhancer={<Plus className="icon-size-default" size={12} />}
        >
          <span className="truncate">{`Add ${predefinedVariant.name}`}</span>
        </SelectableChip>
      </Tooltip>
    );
  });
};

const AnimationToggle: React.FC<{ component: Component }> = ({ component }) => {
  const applyComponentAction = useApplyComponentAction();

  const isAnimated = Boolean(
    useEditorSelector(selectAnimateVariantTransitions),
  );

  return (
    <SwitchWithDescription
      size="sm"
      isOn={isAnimated}
      label="Animate State Transitions"
      layoutClassName="mt-2 mb-2"
      onChange={(checked) => {
        applyComponentAction({
          type: "setProps",
          componentId: component.id,
          value: {
            style: {
              __animateVariantTransitions: checked,
            },
          },
          allVariants: true,
        });
      }}
    />
  );
};

/**
 * Component that renders the add variant menu.
 */
const AddVariantMenu: React.FC<
  React.PropsWithChildren<{
    onSelect(variantData: VariantData): void;
  }>
> = ({ onSelect, children }) => {
  const predefinedVariants = useEditorSelector(
    selectPredefinedVariantsForDraftComponent,
  );

  const getMenuItems = () => {
    const items: MenuItem[] = [];

    if (predefinedVariants.length > 0) {
      items.push({
        items: predefinedVariants.map((variant) => ({
          id: variant.name,
          title: variant.name,
          variant: "default",
          size: "sm",
          onSelect: () => {
            onSelect({
              query: variant.query,
              name: variant.name,
              componentActionType: "addPredefinedVariant",
              predefinedVariantType: variant.predefinedVariantType,
            });
          },
          type: "leaf",
        })),
        type: "section",
      });
    }

    items.push({
      items: [
        {
          id: "custom",
          title: "Add Custom",
          type: "leaf",
          variant: "default",
          size: "sm",
          onSelect: () => {
            onSelect({
              query: null,
              name: NEW_STATE_NAME,
              componentActionType: "addVariant",
            });
          },
        },
      ],
      type: "section",
    });

    return items;
  };

  return (
    <Menu
      items={getMenuItems()}
      customWidth={140}
      contentClassNames="truncate"
      trigger={
        <MenuTrigger asChild>
          <div className="flex items-center justify-center">{children}</div>
        </MenuTrigger>
      }
    />
  );
};

/**
 * Component that renders the edit variant menu, where variant name
 * and conditions can be updated.
 */
const EditVariantMenu: React.FC<{
  variant: ReploState;
  variants: ReploState[];
  onDelete?: () => void;
}> = ({ variant, onDelete, variants }) => {
  const setDraftElement = useSetDraftElement();
  const applyComponentAction = useApplyComponentAction();

  const draftComponentId = useEditorSelector(selectDraftComponentId);

  const variantId = variant.id;

  const inputProps = useOverridableInput({
    value: variant.name,
    onValueChange: React.useCallback(
      (newName: string) => {
        applyComponentAction({
          componentId: draftComponentId,
          type: "updateVariant",
          value: {
            variant: (variant) => {
              return variantId === variant.id && variant.name !== newName
                ? { ...variant, name: newName }
                : variant;
            },
          },
        });
      },
      [draftComponentId, variantId, applyComponentAction],
    ),
  });

  const handleDelete = () => {
    applyComponentAction({
      type: "deleteVariant",
      value: { variantId: variant.id },
    });
    onDelete?.();

    const deletedVariantId = variant.id;
    const deletedVariantIndex = variants.findIndex(
      (v) => v.id === deletedVariantId,
    );

    const previousVariantIndex = Math.max(deletedVariantIndex - 1, 0);
    const previousVariantId = variants[previousVariantIndex]?.id;

    setDraftElement({ variantId: previousVariantId });
  };

  return (
    <div className="flex min-h-[230px] flex-col justify-between">
      <div className="flex flex-1 flex-col gap-2">
        <div id="variant-name" className="flex flex-row items-center">
          <ModifierLabel label="Name" UNSAFE_className="text-muted" />
          <Input id="variant-name" autoFocus {...inputProps} />
        </div>
        <EditVariantConditions variant={variant} />
      </div>
      <DeprecatedButton
        variant="danger"
        onClick={handleDelete}
        layoutClassName="w-full"
      >
        Delete State
      </DeprecatedButton>
    </div>
  );
};

/**
 * Component that renders proper field to edit variant conditions.
 */
const EditVariantConditions: React.FC<{ variant: ReploState }> = ({
  variant,
}) => {
  const applyComponentAction = useApplyComponentAction();

  const draftComponentId = useEditorSelector(selectDraftComponentId);
  const conditionFields = useEditorSelector(
    selectDraftComponentConditionFields,
  );
  const activeCondition = variant.query?.statements?.[0] ?? null;
  const { operatorOptions, editorConfig } =
    mapNull(activeCondition?.field, (field) => {
      return getConditionFieldEditorData(field);
    }) ?? {};

  const updateConditionOperator = React.useCallback(
    (operator: Operator) => {
      applyComponentAction({
        componentId: draftComponentId,
        type: "addVariantTriggerStatement",
        value: {
          variantId: variant.id,
          statement: {
            ...activeCondition!,
            operator,
          },
        },
      });
    },
    [activeCondition, applyComponentAction, draftComponentId, variant.id],
  );

  const updateConditionValue = React.useCallback(
    (value: Operator) => {
      applyComponentAction({
        componentId: draftComponentId,
        type: "addVariantTriggerStatement",
        value: {
          variantId: variant.id,
          statement: {
            ...activeCondition!,
            value,
          },
        },
      });
    },
    [activeCondition, applyComponentAction, draftComponentId, variant.id],
  );

  const removeCondition = (value: ConditionStatement) => {
    return applyComponentAction({
      componentId: draftComponentId,
      type: "deleteVariantTriggerStatement",
      value: {
        statementId: value.id,
        variantId: variant.id,
      },
    });
  };

  // Handles how to update the condition value when the field changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: Disable exhaustive deps for now
  const onConditionFieldChange = React.useCallback(
    (field: ConditionField) => {
      const { operatorOptions, defaultValue } =
        getConditionFieldEditorData(field);

      const id = activeCondition?.id ?? uuidv4();

      if (operatorOptions.length === 0) {
        return applyComponentAction({
          type: "addVariantTriggerStatement",
          value: {
            variantId: variant.id,
            statement: {
              field,
              id,
              operator: null,
              value: null,
            },
          },
        });
      }

      const [operator] = operatorOptions.some(
        (option) => option.value === activeCondition?.operator,
      )
        ? [activeCondition!.operator, activeCondition!.value]
        : [operatorOptions[0]!.value, defaultValue];

      return applyComponentAction({
        type: "addVariantTriggerStatement",
        value: {
          variantId: variant.id,
          statement: {
            field,
            id,
            operator: operator as Operator,
            // NOTE (Fran 2024-09-12): We need to reset the value when the condition field changes to avoid
            // issues with unsupported values across different types of condition fields.
            value: [],
          },
        },
      });
    },
    [activeCondition],
  );

  // Note (Evan, 2023-11-08): Here we check if the field of the active condition is valid, so that
  // states that get copied over don't display if they're not valid in their new context. (conditionFields
  // is dependent on context). We also fall back to null rather than undefined, since undefined would cause
  // the Selectable to revert to uncontrolled state.
  const fieldValueToDisplay =
    activeCondition?.field && conditionFields.includes(activeCondition?.field)
      ? activeCondition.field
      : null;

  return (
    <div className="flex w-full flex-1 flex-row">
      <ModifierLabel label="Show If" UNSAFE_className="text-muted pt-1" />
      <div className="flex w-full flex-col gap-2 truncate">
        <Selectable
          value={fieldValueToDisplay}
          options={conditionFields.map((value) => {
            return {
              label: getConditionFieldEditorData(value)?.displayName,
              value,
            };
          })}
          areOptionsSearchable={conditionFields.length > 7}
          size="sm"
          onSelect={onConditionFieldChange}
          placeholder="Select a condition"
          popoverLayoutClassName="w-56"
          dropdownAlign="end"
          triggerStartEnhancer={
            fieldValueToDisplay && (
              <Badge
                type="character"
                character={getConditionFieldEditorData(
                  fieldValueToDisplay,
                )?.displayName?.charAt(0)}
              />
            )
          }
          onRemove={
            activeCondition === null
              ? undefined
              : () => removeCondition(activeCondition)
          }
        />
        {!isEmpty(operatorOptions) && editorConfig && (
          <>
            <Selectable
              value={activeCondition?.operator ?? undefined}
              options={operatorOptions}
              onSelect={updateConditionOperator}
              popoverLayoutClassName="w-48"
              dropdownAlign="end"
              triggerStartEnhancer={
                activeCondition?.operator && (
                  <Badge
                    type="character"
                    character={
                      operationToSymbol[activeCondition.operator as Operator]
                    }
                  />
                )
              }
            />
            <EditVariantConditionValue
              type={editorConfig.type}
              value={activeCondition?.value}
              onChange={updateConditionValue}
              placeholder={
                hasOwnProperty(editorConfig, "placeholder")
                  ? String(editorConfig.placeholder)
                  : null
              }
            />
          </>
        )}
      </div>
    </div>
  );
};

/**
 * Component that renders the variant condition value field, which
 * changes depending on the condition field type.
 */
const EditVariantConditionValue: React.FC<EditVariantConditionValueProps> = ({
  type,
  value,
  onChange,
  placeholder,
}) => {
  switch (type) {
    case "input":
      return (
        <ConditionFieldEditorValueInput
          placeholder={placeholder}
          value={value}
          onChange={onChange}
        />
      );
    case "inputWithSteps":
      return (
        <InputWithSteps
          value={value}
          onChange={onChange}
          placeholder={placeholder}
        />
      );
    case "productVariant":
      return (
        <ProductVariantSelectionCombobox
          align="end"
          value={value}
          onSubmit={onChange}
        />
      );
    case "templateProduct":
      return (
        <ProductSelectionCombobox
          onChange={onChange}
          value={value}
          isMultiselect={true}
          sideOffset={85}
        />
      );
    case "product":
      return (
        <ProductSelectionCombobox
          onChange={onChange}
          value={value}
          isMultiselect={true}
          sideOffset={85}
        />
      );
    default:
      return null;
  }
};

function ConditionFieldEditorValueInput({
  placeholder,
  value: providedValue,
  onChange,
}: {
  placeholder: string | null;
  value: any;
  onChange(value: any): void;
}) {
  const inputProps = useOverridableInput({
    value: providedValue ?? "",
    onValueChange: onChange,
  });
  return <Input placeholder={placeholder} {...inputProps} />;
}

function getValidatedNumericValue(value: string | number) {
  if (value == null || value === "") {
    return "";
  }
  const _value = isNumber(value) ? value : Number.parseInt(value, 10);
  return String(Number.isNaN(_value) ? 0 : _value);
}

const InputWithSteps: React.FC<
  Pick<EditVariantConditionValueProps, "value" | "onChange" | "placeholder">
> = ({ value: providedValue, onChange, placeholder }) => {
  const validatedValue = getValidatedNumericValue(providedValue);

  const _onChange = React.useCallback(
    (value: string) => {
      const validated = getValidatedNumericValue(value);
      if (validated === "" || !Number.isNaN(value)) {
        // NOTE (Chance 2023-12-08): We only want to use the pixel value if
        // we've got a valid number. Empty inputs are just empty values.
        onChange?.(value ?? `${value}px`);
      }
    },
    [onChange],
  );

  const [value, setValue] = useOverridableState(
    validatedValue,
    useDebouncedCallback(_onChange, 300),
  );

  return (
    <div className="flex gap-2">
      <Input
        type="number"
        placeholder={placeholder}
        value={value}
        onChange={(event) => {
          setValue(getValidatedNumericValue(event.target.value));
        }}
      />
      <ButtonGroupComponent
        options={[
          {
            label: <Minus size={12} />,
            onClick: () => {
              const number = value ? Number.parseInt(value, 10) : 0;
              if (number > 0) {
                setValue(String(number - 1));
              }
            },
            tooltipText: "Subtract 1",
          },
          {
            label: <Plus size={12} />,
            onClick: () => {
              const number = value ? Number.parseInt(value, 10) : -1;
              setValue(String(number + 1));
            },
            tooltipText: "Add 1",
          },
        ]}
      />
    </div>
  );
};

export default VariantModifier;
